import { useMemo } from 'react';
import { UseMutationOptions, useQueryClient, useMutation } from 'react-query';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { FetchError } from 'src/types/TApiQueries';
import {
  addUser, editUser, reassignInvite, removeMember, resendSignup, restoreMember,
} from '../functions/userManagementApi';
import { queryFnFetchErrorWrapper } from '../utils/errorHandlers';
import { queryNames } from '../utils/queryNames';

export function useAddUserManagementMutation(mutationOptions?: UseMutationOptions<any, FetchError, any>) {
  const accessToken = useAccessToken();

  const mutationFn = useMemo(() => queryFnFetchErrorWrapper((data: any) => addUser(data, accessToken)), [accessToken]);

  return useMutation(mutationFn, {
    ...mutationOptions,
    onSuccess: async (result, ...args) => {
      mutationOptions?.onSuccess?.(result, ...args);
    },
  });
}

export function useEditUserManagementMutation(
  userId: number,
  mutationOptions?: UseMutationOptions<any, FetchError, any>,
) {
  const queryClient = useQueryClient();
  const accessToken = useAccessToken();

  const mutationFn = useMemo(
    () => queryFnFetchErrorWrapper((data: any) => editUser(userId, data, accessToken)),
    [userId, accessToken],
  );

  return useMutation(mutationFn, {
    ...mutationOptions,
  });
}

export function useResendSignupMutation(
  userId: number,
  mutationOptions?: UseMutationOptions<any, FetchError, void>,
) {
  const accessToken = useAccessToken();

  const mutationFn = useMemo(
    () => queryFnFetchErrorWrapper(() => resendSignup(userId, accessToken)),
    [userId, accessToken],
  );

  return useMutation(mutationFn, {
    ...mutationOptions,
  });
}

// --- Seat lifecycle (plan-structure v2 Phase 7a) ---------------------------

/** Remove a member from the team — frees their seat, keeps their assets. */
export function useRemoveMemberMutation(
  userId: number,
  mutationOptions?: UseMutationOptions<any, FetchError, void>,
) {
  const accessToken = useAccessToken();

  const mutationFn = useMemo(
    () => queryFnFetchErrorWrapper(() => removeMember(userId, accessToken)),
    [userId, accessToken],
  );

  return useMutation(mutationFn, { ...mutationOptions });
}

/** Undo a removal. Consumes a seat, so it can 403 `plan_upgrade_required`. */
export function useRestoreMemberMutation(
  userId: number,
  mutationOptions?: UseMutationOptions<any, FetchError, void>,
) {
  const accessToken = useAccessToken();

  const mutationFn = useMemo(
    () => queryFnFetchErrorWrapper(() => restoreMember(userId, accessToken)),
    [userId, accessToken],
  );

  return useMutation(mutationFn, { ...mutationOptions });
}

/** Point an unaccepted invitation at a different email address. */
export function useReassignInviteMutation(
  userId: number,
  mutationOptions?: UseMutationOptions<any, FetchError, string>,
) {
  const accessToken = useAccessToken();

  const mutationFn = useMemo(
    () => queryFnFetchErrorWrapper((email: string) => reassignInvite(userId, email, accessToken)),
    [userId, accessToken],
  );

  return useMutation(mutationFn, { ...mutationOptions });
}
